import moment from 'moment';
import { isEmpty } from 'underscore';
import { parseUsOrIso } from './date';

/**
 * Billed-hours calculation for the shift selector, kept out of the component so
 * the rules are testable and can be checked against the API, which stores the
 * authoritative figure (VisitRequestDate::billableHours).
 *
 * The rules, and where they come from:
 *  - 24/7 sites are always open, so nothing is chargeable — any request type.
 *  - A Retail General visit books no guard at all, so nothing is chargeable
 *    whatever the hour.
 *  - Guarding books an officer for the whole shift, so the site's opening hours
 *    produce no deduction.
 *  - Everything else (visits) is charged only for the hours falling OUTSIDE the
 *    site's open days/hours.
 *  - A bank holiday suspends the site's open window for that date, so those
 *    hours are charged whatever the shift type.
 */

export interface BilledShift {
    date: string;
    startTime?: string | null | undefined;
    endTime?: string | null | undefined;
}

export interface BilledOpenDay {
    day: string;
    startTime: string;
    endTime: string;
}

export interface BilledHoursOptions {
    enabled24_7: boolean;
    isGuardingRequest?: boolean;
    /** A General Visit on a retail site: an access request with no guard booked. */
    isRetailGeneralVisit?: boolean;
    openDays?: BilledOpenDay[] | undefined;
    /**
     * ISO YYYY-MM-DD. A bank holiday suspends the site's open window for that
     * date, so the shift bills in full — mirrors the API's
     * VisitRequestDate::billableHours().
     */
    bankHolidays?: string[] | undefined;
}

/**
 * A shift's full length in hours. An end time before the start time runs past
 * midnight.
 *
 * Compares the full time, not just the hour: 10:45 -> 10:15 shares an hour but
 * still crosses midnight, and an hour-only check left it as a NEGATIVE
 * duration. The API decides the same way ($end->lt($start)), so the figure
 * shown here and the figure billed agree.
 */
const fullShiftHours = (shifts: BilledShift[]): number => shifts.reduce((acc, curr) => {
    if (curr.startTime && curr.endTime) {
        const start = moment(curr.startTime, 'HH:mm');
        const end = moment(curr.endTime, 'HH:mm');
        if (end.isBefore(start)) {
            end.add(1, 'day');
        }
        return acc + (moment.duration(end.diff(start)).asMinutes() / 60);
    }
    return acc;
}, 0);

export const calculateBilledHours = (
    selectedShifts: BilledShift[],
    {
        enabled24_7,
        isGuardingRequest = false,
        isRetailGeneralVisit = false,
        openDays,
        bankHolidays,
    }: BilledHoursOptions,
): number => {
    // If enabled24_7 is true, every time is an open day, so return 0.
    if (enabled24_7) {
        return 0;
    }

    // A Retail General visit involves no guard, officer or scheduling, so it is
    // never chargeable. The API applies the same rule, in
    // CompletedVisitsHoursCalculator and VisitRequest::recomputeTotalHours.
    if (isRetailGeneralVisit) {
        return 0;
    }

    // Guarding: the whole shift, with no open-day deduction.
    if (isGuardingRequest) {
        return Number(fullShiftHours(selectedShifts).toFixed(2));
    }

    if (isEmpty(openDays)) {
        return Number(fullShiftHours(selectedShifts).toFixed(2));
    }

    const bankHolidayDates = new Set(bankHolidays ?? []);

    // Visits: only the hours outside the site's open window are chargeable.
    const billable = selectedShifts.reduce((acc, curr) => {
        let openDay = openDays?.find((day) => day.day === parseUsOrIso(curr.date).format('dddd'));

        // A shift running past midnight may land on the NEXT day's open window.
        // Compared as times rather than strings, so an unpadded "9:00" can't
        // sort above "10:15".
        const crossesMidnight = !!curr.startTime
            && !!curr.endTime
            && moment(curr.endTime, 'HH:mm').isBefore(moment(curr.startTime, 'HH:mm'));

        // The store's own hours do not apply on a bank holiday, so the shift is
        // charged as though the site were closed.
        if (openDay && bankHolidayDates.has(parseUsOrIso(curr.date).format('YYYY-MM-DD'))) {
            openDay = undefined;
        }

        if (!openDay && crossesMidnight) {
            const nextDate = parseUsOrIso(curr.date).add(1, 'day');
            const nextDay = nextDate.format('dddd');
            openDay = bankHolidayDates.has(nextDate.format('YYYY-MM-DD'))
                ? undefined
                : openDays?.find((day) => day.day === nextDay);
        }

        if (!curr.startTime || !curr.endTime) {
            return acc;
        }

        const shiftStart = moment(curr.startTime, 'HH:mm');
        const shiftEnd = moment(curr.endTime, 'HH:mm');
        if (shiftEnd.isBefore(shiftStart)) {
            shiftEnd.add(1, 'day');
        }

        const totalShiftMinutes = moment.duration(shiftEnd.diff(shiftStart)).asMinutes();

        // Closed that day — the entire shift is billable.
        if (!openDay) {
            return acc + (totalShiftMinutes / 60);
        }

        const openStart = moment(openDay.startTime, 'HH:mm');
        const openEnd = moment(openDay.endTime, 'HH:mm');
        const effectiveStart = moment.max(shiftStart, openStart);
        const effectiveEnd = moment.min(shiftEnd, openEnd);

        // No overlap with the open window — the entire shift is billable.
        if (effectiveEnd.isBefore(effectiveStart)) {
            return acc + (totalShiftMinutes / 60);
        }

        const overlapMinutes = moment.duration(effectiveEnd.diff(effectiveStart)).asMinutes();
        return acc + ((totalShiftMinutes - overlapMinutes) / 60);
    }, 0);

    return Number(billable.toFixed(2));
};

export default calculateBilledHours;
